import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import ICategory from "../../entities/ICategory";
import IProduct from "../../entities/IProduct";
import { ICollectionProduct } from "../../entities/ICollection";
import CategoryService from "../../services/CategoryService";
import CollectionService from "../../services/CollectionService";
import ShopService from "../../services/ShopService";
import AuthContext from "../../store/auth-context";
import { unwrapList, getErrorMessage, resolveImageUrl } from "../../services/apiUtils";

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box
    sx={{
      background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
      color: "#fff",
      px: 2.5,
      py: 1.75,
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    {icon}
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
  </Box>
);

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow:
    "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  overflow: "hidden",
};

const AddCollection: React.FC = () => {
  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [scheduleError, setScheduleError] = useState<string>("");

  const [categories, setCategories] = useState<Array<ICategory>>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | "">("");
  const [categoryProducts, setCategoryProducts] = useState<Array<IProduct>>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());

  const [collectionItems, setCollectionItems] = useState<Map<number, IProduct>>(new Map());

  const [ownerShopId, setOwnerShopId] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const customerId = authCtx.user?.userId;
    if (!customerId) return;
    ShopService.getShopsByCustomerId(customerId)
      .then((res: any) => {
        const shops = unwrapList<any>(res);
        if (shops.length > 0) setOwnerShopId(shops[0].shopId);
      })
      .catch(() => {});
  }, [authCtx.user?.userId]);

  useEffect(() => {
    setCategoriesLoading(true);
    CategoryService.getCategory()
      .then((res: any) => setCategories(unwrapList<ICategory>(res)))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, []);

  const handleCategoryChange = (e: SelectChangeEvent<number | string>) => {
    const categoryId = Number(e.target.value);
    setSelectedCategoryId(categoryId);
    setSelectedProductIds(new Set());
    const category = categories.find((c) => c.categoryId === categoryId);

    const products = Array.isArray(category?.productList) ? (category!.productList as IProduct[]) : [];
    setCategoryProducts(products);
  };

  const toggleProduct = (productId: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const handleAddSelectedToCollection = () => {
    if (selectedProductIds.size === 0) {
      setSnackbar({ open: true, message: "Select at least one product first.", severity: "error" });
      return;
    }
    setCollectionItems((prev) => {
      const next = new Map(prev);
      categoryProducts.forEach((p) => {
        if (selectedProductIds.has(p.productId)) next.set(p.productId, p);
      });
      return next;
    });
    setSelectedProductIds(new Set());
    setSnackbar({ open: true, message: "Products added to the collection.", severity: "success" });
  };

  const handleRemoveItem = (productId: number) => {
    setCollectionItems((prev) => {
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  };

  const items = useMemo(() => Array.from(collectionItems.values()), [collectionItems]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    setNameTouched(true);
    if (!trimmedName) {
      setSnackbar({ open: true, message: "Please enter a collection name.", severity: "error" });
      return;
    }
    if (items.length === 0) {
      setSnackbar({ open: true, message: "Add at least one product to the collection.", severity: "error" });
      return;
    }
    if (startsAt && endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setScheduleError("End date/time must be after the start date/time.");
      setSnackbar({ open: true, message: "End date/time must be after the start date/time.", severity: "error" });
      return;
    }
    setScheduleError("");
    setSaving(true);
    try {
      const productDtos: Array<ICollectionProduct> = items.map((p) => ({ productId: p.productId }));
      await CollectionService.createCollection(
        trimmedName,
        productDtos,
        ownerShopId,
        CollectionService.toApiDateTime(startsAt),
        CollectionService.toApiDateTime(endsAt)
      );
      setSnackbar({ open: true, message: "Collection created successfully.", severity: "success" });
      setTimeout(() => navigate("/collections"), 600);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, "Could not create the collection. Please try again."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [name, items, ownerShopId, navigate, startsAt, endsAt]);

  return (
    <Box>
      <Box sx={cardSx}>
        <SectionHeader icon={<CollectionsBookmarkRoundedIcon />} title="Collection Information" />
        <Box sx={{ p: 2.5 }}>
          <TextField
            fullWidth
            label="Collection Name"
            placeholder="e.g. Flash Sale 2, Winter Collection"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && !name.trim()}
            helperText={
              nameTouched && !name.trim()
                ? "Name is required"
                : name.trim()
                ? `Slug: ${CollectionService.slugify(name)}`
                : " "
            }
          />

          <Box
            display="grid"
            gridTemplateColumns={isNonMobile ? "repeat(2, 1fr)" : "1fr"}
            gap="16px"
            mt={2}
          >
            <TextField
              fullWidth
              type="datetime-local"
              label="Start Date & Time (optional)"
              InputLabelProps={{ shrink: true }}
              value={startsAt}
              onChange={(e) => {
                setStartsAt(e.target.value);
                setScheduleError("");
              }}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="End Date & Time (optional)"
              InputLabelProps={{ shrink: true }}
              value={endsAt}
              onChange={(e) => {
                setEndsAt(e.target.value);
                setScheduleError("");
              }}
              error={Boolean(scheduleError)}
              helperText={scheduleError || " "}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Agar aap start/end date set karain gay tou yeh collection us waqt ke baad khud
            website par inactive/hide ho jayegi. Agar koi date nahi daali, tou collection tab
            tak active rahegi jab tak aap ise khud remove na karein.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ ...cardSx, mt: "16px" }}>
        <SectionHeader icon={<CategoryRoundedIcon />} title="Add Products" />
        <Box sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick a category to see its products, tick the ones you want, then add them to this
            collection. You can repeat this with as many categories as you like.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={categoriesLoading}>
            <InputLabel id="collection-category-label">Source Category</InputLabel>
            <Select
              labelId="collection-category-label"
              label="Source Category"
              value={selectedCategoryId}
              onChange={handleCategoryChange}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.categoryId} value={cat.categoryId}>
                  {cat.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {categoriesLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={22} />
            </Box>
          ) : selectedCategoryId === "" ? (
            <Typography variant="body2" color="text.secondary">
              Select a category above to see its products.
            </Typography>
          ) : categoryProducts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This category has no products yet.
            </Typography>
          ) : (
            <Box sx={{ border: "1px solid #eee", borderRadius: "10px", maxHeight: 320, overflow: "auto" }}>
              <List dense disablePadding>
                {categoryProducts.map((p) => (
                  <ListItem
                    key={p.productId}
                    button
                    onClick={() => toggleProduct(p.productId)}
                    divider
                  >
                    <ListItemIcon>
                      <Checkbox edge="start" checked={selectedProductIds.has(p.productId)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemAvatar>
                      <Avatar
                        variant="rounded"
                        src={resolveImageUrl(p.thumbnail) || undefined}
                        sx={{ bgcolor: "#EEF0FB" }}
                      >
                        <Inventory2RoundedIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={p.title || "Untitled"}
                      secondary={`Rs. ${Number(p.price || 0).toFixed(2)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {categoryProducts.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="outlined"
                startIcon={<AddShoppingCartRoundedIcon />}
                disabled={selectedProductIds.size === 0}
                onClick={handleAddSelectedToCollection}
              >
                Add {selectedProductIds.size > 0 ? selectedProductIds.size : ""} to Collection
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ ...cardSx, mt: "16px" }}>
        <SectionHeader icon={<CheckCircleRoundedIcon />} title={`Collection Items (${items.length})`} />
        <Box sx={{ p: 2.5 }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No products added yet. Select products above and add them to this collection.
            </Typography>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isNonMobile ? "repeat(2, 1fr)" : "1fr"}
              gap="10px"
            >
              {items.map((p) => (
                <Chip
                  key={p.productId}
                  avatar={
                    <Avatar src={resolveImageUrl(p.thumbnail) || undefined}>
                      <Inventory2RoundedIcon fontSize="small" />
                    </Avatar>
                  }
                  label={p.title || `Product #${p.productId}`}
                  onDelete={() => handleRemoveItem(p.productId)}
                  deleteIcon={<CloseRoundedIcon />}
                  sx={{ justifyContent: "space-between", py: 2.5, height: "auto" }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="end" mt="20px">
        <Button
          color="secondary"
          variant="contained"
          disabled={saving}
          onClick={handleSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CollectionsBookmarkRoundedIcon />}
          sx={{ borderRadius: "10px", px: 3, boxShadow: "0px 4px 10px rgba(33,150,243,0.35)" }}
        >
          {saving ? "Creating..." : "Create Collection"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddCollection;
